import { BUILD_BRANCH, BUILD_LOG, BUILD_SHA, BUILD_TIME } from '@/config/build';
import { firstName, formatWarsawTime, isThisBuild, type BuildLogEntry } from '@/config/buildLog';
import { pokazujPaneleDev } from '@/config/env';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

const STAMP = 'ml-1.5 select-none font-mono text-[10px] leading-none text-muted-foreground/50';

/**
 * Tiny build identifier shown next to the logo so we can tell at a glance which
 * build is live — invaluable when a change is pure logic and the UI looks
 * identical before/after a deploy. Hover shows the branch + build time.
 *
 * On the developers' sites (`pokazujPaneleDev`) it also opens „Dev · recent builds”: the last
 * commits baked into this bundle, so Maciek can tell whether he is on the newest build and what
 * went into it (Maciek 20.10, PreLaunchChecklist 2.15). English like the other dev panels (Conv 2
 * exception). The hostname check is UX only (Conv 29) — what actually keeps commit subjects off
 * stage and prod is that their bundles are built with an empty list (`buildLogToInject`).
 */
const BuildStamp = ({
  log = BUILD_LOG,
  widoczny = pokazujPaneleDev(),
}: { log?: BuildLogEntry[]; widoczny?: boolean }) => {
  const title = [BUILD_BRANCH, BUILD_TIME].filter(Boolean).join(' · ');

  if (!widoczny || log.length === 0) {
    return (
      <span title={title || undefined} aria-hidden="true" className={STAMP}>
        {BUILD_SHA}
      </span>
    );
  }

  const zbudowano = formatWarsawTime(BUILD_TIME);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={title || undefined}
          aria-label="Dev · recent builds"
          className={cn(
            STAMP,
            'strefa-dotyku relative cursor-pointer rounded-sm hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          )}
        >
          {BUILD_SHA}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={16}
        data-testid="build-log-dev-panel"
        className="w-[min(28rem,calc(100vw-2rem))] max-h-[60vh] overflow-y-auto border-dashed border-warning bg-warning-soft p-0 text-xs text-warning-soft-foreground"
      >
        <div className="space-y-1 p-3">
          <p className="font-semibold uppercase tracking-wide">Dev · recent builds</p>
          <p className="break-words leading-snug">
            This build: <span className="font-mono font-semibold">{BUILD_SHA}</span>
            {BUILD_BRANCH && <> · {BUILD_BRANCH}</>}
            {zbudowano && <> · built {zbudowano}</>}
          </p>
        </div>

        <ol>
          {log.map((c) => {
            const ten = isThisBuild(c.hash, BUILD_SHA);
            return (
              <li
                key={c.hash}
                data-testid="build-log-row"
                data-this-build={ten || undefined}
                data-merge={c.merge || undefined}
                className={cn('border-t border-warning/30 px-3 py-2', c.merge && 'opacity-60')}
              >
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span className="font-mono font-semibold">{c.hash}</span>
                  <span>{formatWarsawTime(c.date)}</span>
                  {c.author && <span>{firstName(c.author)}</span>}
                  {ten && (
                    <span className="rounded bg-warning px-1 text-[10px] font-semibold uppercase leading-4 text-warning-foreground">
                      this build
                    </span>
                  )}
                  {c.merge && (
                    <span className="rounded border border-warning px-1 text-[10px] uppercase leading-4">merge</span>
                  )}
                </div>
                <p className="mt-0.5 break-words leading-snug">{c.subject}</p>
              </li>
            );
          })}
        </ol>

        <p className="border-t border-warning/30 px-3 py-2 text-[11px] leading-snug">
          Shown only on developer sites. Newer pushes may still be deploying — refresh to load them.
        </p>
      </PopoverContent>
    </Popover>
  );
};

export default BuildStamp;
